/**
 * Calendar-date formatting for API parameters.
 *
 * The backend's date parameters are `DateOnly` — a calendar day, with no time
 * and no zone. `date.toISOString().slice(0, 10)` is the tempting one-liner for
 * producing one and it is wrong: `toISOString` converts to UTC first, so the
 * string can name a different day than the user is looking at.
 *
 *   - A date the picker built at local midnight is already the *previous* day
 *     in UTC anywhere east of Greenwich — so it is off by one every time.
 *   - `new Date()` crosses the boundary the other way every evening west of
 *     Greenwich: at 18:00 in California, UTC is already tomorrow.
 *
 * Read the local calendar fields instead and the string always names the day
 * the user picked.
 */
export function toLocalDateString(d: Date): string {
  return `${d.getFullYear()}-${`${d.getMonth() + 1}`.padStart(2, '0')}-${`${d.getDate()}`.padStart(2, '0')}`;
}

/** Today, on the user's calendar. */
export function todayLocalDateString(): string {
  return toLocalDateString(new Date());
}

/**
 * The other direction: a `YYYY-MM-DD` API date as a `Date` on the user's own
 * calendar, for a control (a Material datepicker) that works in `Date`.
 *
 * `new Date('2026-07-31')` is the same trap in reverse — the string is read as
 * UTC midnight, so anywhere west of Greenwich the `Date` names the day before.
 * Building it from the parts keeps the day the user picked.
 */
export function parseLocalDateString(value: string): Date | null {
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!parts) return null;
  return new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]));
}

/** `days` from today (negative for the past), on the user's calendar. */
export function localDateStringOffsetDays(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return toLocalDateString(d);
}
